import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Briefcase, DollarSign, Route, Users, FileText, TrendingUp, 
  ChevronRight, Calendar, MapPin, Clock
} from 'lucide-react';
import axios from 'axios';

const ClientDashboard = () => {
  const { token, API } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { 
      label: 'Jobs Today', 
      value: dashboard?.jobs_today || 0,
      subValue: `${dashboard?.jobs_completed_today || 0} completed`,
      icon: Briefcase, 
      color: 'bg-blue-500',
      link: '/app/jobs'
    },
    { 
      label: 'Outstanding', 
      value: `$${(dashboard?.total_outstanding || 0).toLocaleString()}`,
      subValue: `${dashboard?.outstanding_invoice_count || 0} invoices`,
      icon: DollarSign, 
      color: 'bg-orange-500',
      link: '/app/invoices'
    },
    { 
      label: 'Routes Today', 
      value: dashboard?.routes_today || 0,
      subValue: 'active routes',
      icon: Route, 
      color: 'bg-emerald-500',
      link: '/app/routes'
    },
    { 
      label: 'Active Crews', 
      value: dashboard?.active_crews || 0,
      subValue: 'in the field',
      icon: Users, 
      color: 'bg-purple-500',
      link: '/app/crews'
    },
  ];

  return (
    <ClientLayout title="Dashboard">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <Link to={stat.link} key={index}>
                <Card 
                  className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-500">{stat.label}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                        <p className="text-xs text-gray-400 mt-1">{stat.subValue}</p>
                      </div>
                      <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                        <stat.icon className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Secondary Stats */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${(dashboard?.monthly_revenue || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center text-emerald-600 text-sm">
                    <TrendingUp className="h-4 w-4 mr-1" />
                    +12%
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Customers</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard?.total_customers || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Properties</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard?.total_properties || 0}</p>
                  </div>
                  <MapPin className="h-8 w-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Estimates & Quick Actions */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Estimates */}
            <Card className="border-gray-200">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Recent Estimates</CardTitle>
                <Link to="/app/estimates">
                  <Button variant="ghost" size="sm" className="text-primary">
                    View All <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {!dashboard?.recent_estimates || dashboard.recent_estimates.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent estimates</p>
                    <Link to="/app/estimates">
                      <Button variant="outline" size="sm" className="mt-3">
                        Create Estimate
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.recent_estimates.map((estimate) => (
                      <div 
                        key={estimate.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">
                              ${estimate.total?.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(estimate.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant={
                          estimate.status === 'approved' ? 'default' :
                          estimate.status === 'sent' ? 'secondary' :
                          estimate.status === 'declined' ? 'destructive' : 'outline'
                        }>
                          {estimate.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <Link to="/app/customers">
                    <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      <span className="text-sm">Add Customer</span>
                    </Button>
                  </Link>
                  <Link to="/app/properties">
                    <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      <span className="text-sm">Add Property</span>
                    </Button>
                  </Link>
                  <Link to="/app/estimates">
                    <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      <span className="text-sm">New Estimate</span>
                    </Button>
                  </Link>
                  <Link to="/app/jobs">
                    <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2">
                      <Briefcase className="h-5 w-5 text-primary" />
                      <span className="text-sm">Create Job</span>
                    </Button>
                  </Link>
                </div>

                <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Today's Schedule</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {dashboard?.jobs_today || 0} jobs scheduled, {dashboard?.routes_today || 0} routes active
                      </p>
                      <Link to="/app/calendar">
                        <Button variant="link" className="px-0 text-primary text-sm h-auto mt-2">
                          View Calendar <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </ClientLayout>
  );
};

export default ClientDashboard;
